/**
 * 직접거래 메시지 컴포넌트
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import './TradeMessage.css';

interface TradeData {
  id: number;
  title: string;
  description?: string;
  agreedPrice?: number;
  status: 'negotiating' | 'in_progress' | 'completed' | 'cancelled';
  clientConfirmed?: boolean;
  freelancerConfirmed?: boolean;
}

interface TradeMessageProps {
  tradeData: TradeData;
  messageType: string;
  isMyMessage: boolean;
  isClient: boolean;
  onAccept?: (tradeId: number) => void;
  onComplete?: (tradeId: number) => void;
  onCancel?: (tradeId: number) => void;
}

const TradeMessage: React.FC<TradeMessageProps> = ({
  tradeData,
  messageType,
  isMyMessage,
  isClient,
  onAccept,
  onComplete,
  onCancel
}) => {
  const { t } = useTranslation();
  const getStatusBadge = () => {
    switch (tradeData.status) {
      case 'negotiating':
        return <span className="trade-status negotiating">{t('chat.negotiating')}</span>;
      case 'in_progress':
        return <span className="trade-status in-progress">{t('tradeMsg.inProgress')}</span>;
      case 'completed':
        return <span className="trade-status completed">{t('tradeMsg.completed')}</span>;
      case 'cancelled':
        return <span className="trade-status cancelled">{t('transactions.statusCancelled')}</span>;
      default:
        return null;
    }
  };

  const renderActions = () => {
    // 거래 시작 메시지 - 상대방만 수락 가능
    if (messageType === 'trade_start' && !isMyMessage && tradeData.status === 'negotiating') {
      return (
        <div className="trade-actions">
          <button className="accept-btn" onClick={() => onAccept?.(tradeData.id)}>
            ✅ {t('tradeMsg.acceptTrade')}
          </button>
          <button className="cancel-btn" onClick={() => onCancel?.(tradeData.id)}>
            ❌ {t('common.reject')}
          </button>
        </div>
      );
    }

    // 거래중 상태 - 완료 버튼 표시
    if (tradeData.status === 'in_progress') {
      const myConfirmed = isClient ? tradeData.clientConfirmed : tradeData.freelancerConfirmed;
      const otherConfirmed = isClient ? tradeData.freelancerConfirmed : tradeData.clientConfirmed;

      if (!myConfirmed) {
        return (
          <div className="trade-actions">
            <button className="complete-btn" onClick={() => onComplete?.(tradeData.id)}>
              🎉 {t('chat.completeTradeTitle')}
            </button>
            <button className="cancel-btn secondary" onClick={() => onCancel?.(tradeData.id)}>
              {t('common.cancel')}
            </button>
          </div>
        );
      } else if (!otherConfirmed) {
        return (
          <div className="trade-waiting">
            <span>✅ {t('tradeMsg.iConfirmed')}</span>
            <span className="waiting-text">{t('tradeMsg.waitingOther')}</span>
          </div>
        );
      }
    }

    return null;
  };

  return (
    <div className={`trade-message ${messageType} ${isMyMessage ? 'my-message' : 'other-message'}`}>
      <div className="trade-card">
        <div className="trade-header">
          <span className="trade-icon">
            {messageType === 'trade_start' && '📋'}
            {messageType === 'trade_accept' && '✅'}
            {messageType === 'trade_complete' && '📝'}
            {messageType === 'trade_confirmed' && '🎉'}
          </span>
          <span className="trade-title">{tradeData.title || t('tradeModal.defaultTitle')}</span>
          {getStatusBadge()}
        </div>

        {tradeData.description && (
          <div className="trade-description">{tradeData.description}</div>
        )}

        {tradeData.agreedPrice && tradeData.agreedPrice > 0 && (
          <div className="trade-price">
            💰 {t('tradeMsg.agreedPrice')}: {t('projects.budgetExact', { v: tradeData.agreedPrice.toLocaleString() })}
            <span className="price-note">{t('tradeMsg.refOnly')}</span>
          </div>
        )}

        {renderActions()}

        <div className="trade-notice">
          ⚠️ {t('tradeModal.noticeDesc')}
        </div>
      </div>
    </div>
  );
};

export default TradeMessage;
